/**
 * AdminClients - Client management page
 * List, search, filter, and manage client accounts
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch, navigate, hasPermission } from './api';

const STATUS_COLORS = {
  active: '#10B981', onboarding: '#3B82F6', suspended: '#F59E0B', churned: '#EF4444',
};

const TIER_COLORS = {
  smb: { bg: 'rgba(0,212,170,0.12)', text: '#00D4AA' },
  midmarket: { bg: 'rgba(59,130,246,0.12)', text: '#5B9DFF' },
  enterprise: { bg: 'rgba(192,132,252,0.12)', text: '#C084FC' },
};

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ company_name: '', contact_name: '', contact_email: '', tier: 'smb', phone: '' });
  const [creating, setCreating] = useState(false);

  const fetchClients = () => {
    adminFetch('/api/admin/clients')
      .then(r => r.json())
      .then(d => { setClients(d.clients || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchClients(); }, []);

  const filtered = clients.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.company_name || '').toLowerCase().includes(q) ||
        (c.primary_contact_name || '').toLowerCase().includes(q) ||
        (c.primary_contact_email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await adminFetch('/api/admin/clients', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowCreate(false);
        setForm({ company_name: '', contact_name: '', contact_email: '', tier: 'smb', phone: '' });
        fetchClients();
      }
    } catch {}
    setCreating(false);
  };

  const s = {
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
    searchInput: {
      padding: '10px 14px', borderRadius: 8, border: '1px solid #2A3444',
      backgroundColor: '#1A2332', color: '#FFF', fontSize: 14, width: 280, outline: 'none',
    },
    filterBtn: (active) => ({
      padding: '8px 16px', borderRadius: 6, border: '1px solid #2A3444', fontSize: 13,
      fontWeight: 500, cursor: 'pointer',
      backgroundColor: active ? '#1E293B' : 'transparent',
      color: active ? '#FFF' : '#8B92A5',
    }),
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700,
      color: '#5A6175', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2A3444',
    },
    td: { padding: '14px 16px', fontSize: 14, color: '#BBBEC7', borderBottom: '1px solid #1A2332' },
    createBtn: {
      padding: '10px 20px', borderRadius: 8, border: 'none',
      background: 'linear-gradient(135deg, #0057FF, #0040CC)', color: '#FFF',
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    modal: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 200,
    },
    modalCard: {
      backgroundColor: '#0F1729', borderRadius: 16, border: '1px solid #2A3444',
      padding: 32, width: 440,
    },
    input: {
      width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #2A3444',
      backgroundColor: '#1A2332', color: '#FFF', fontSize: 14, outline: 'none', boxSizing: 'border-box',
    },
    field: { marginBottom: 16 },
    label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#8B92A5', marginBottom: 4 },
  };

  return (
    <AdminShell title="Clients">
      <div style={s.topBar}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input style={s.searchInput} placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} />
          {['all', 'active', 'onboarding', 'suspended'].map(f => (
            <button key={f} style={s.filterBtn(filter === f)} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {hasPermission('clients', 'create') && (
          <button style={s.createBtn} onClick={() => setShowCreate(true)}>+ Add Client</button>
        )}
      </div>

      <div style={{ backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>
            {clients.length === 0 ? 'No clients yet. Add your first client or convert a prospect.' : 'No clients match your search.'}
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Company</th>
                <th style={s.th}>Tier</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Contact</th>
                <th style={s.th}>Since</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const tier = TIER_COLORS[c.tier] || TIER_COLORS.smb;
                const statusColor = STATUS_COLORS[c.status] || '#5A6175';
                return (
                  <tr key={c.id}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, color: '#FFF' }}>{c.company_name}</div>
                    </td>
                    <td style={s.td}>
                      <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', backgroundColor: tier.bg, color: tier.text }}>
                        {c.tier || 'smb'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', backgroundColor: `${statusColor}20`, color: statusColor }}>
                        {c.status}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ fontSize: 13 }}>{c.primary_contact_name}</div>
                      <div style={{ fontSize: 11, color: '#5A6175' }}>{c.primary_contact_email}</div>
                    </td>
                    <td style={s.td}>{formatDate(c.created_at)}</td>
                    <td style={s.td}>
                      <button
                        style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #2A3444', backgroundColor: 'transparent', color: '#5B9DFF', fontSize: 12, cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/clients/${c.id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Client Modal */}
      {showCreate && (
        <div style={s.modal} onClick={() => setShowCreate(false)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600 }}>Add New Client</h3>
            <form onSubmit={handleCreate}>
              <div style={s.field}>
                <label style={s.label}>Company Name</label>
                <input style={s.input} value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Contact Name</label>
                <input style={s.input} value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Contact Email</label>
                <input style={s.input} type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} required />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Tier</label>
                  <select style={s.input} value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })}>
                    <option value="smb">SMB</option>
                    <option value="midmarket">Mid-Market</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Phone</label>
                  <input style={s.input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="button" style={{ ...s.createBtn, background: 'transparent', border: '1px solid #2A3444', color: '#8B92A5', flex: 1 }} onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" style={{ ...s.createBtn, flex: 1 }} disabled={creating}>{creating ? 'Creating...' : 'Create Client'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
